import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, Loader2, GripVertical, Eye, EyeOff, Sparkles, Image as ImageIcon, Type } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/services/adminApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url?.startsWith("/api/") ? `${BACKEND}${url}` : url);

const emptyForm = { title: "", description: "", image_url: "", link_type: "none", link_value: "", location: "home", start_date: "", end_date: "", active: true, order: 0 };
const locationLabels = { home: "Ana səhifə", movies: "Filmlər", series: "Seriallar", all: "Bütün səhifələr" };

// datetime-local (local tz) <-> UTC "YYYY-MM-DDTHH:MM" stored on server
const toUtc = (local) => (local ? new Date(local).toISOString().slice(0, 16) : null);
const toLocal = (utc) => {
  if (!utc) return "";
  const d = new Date(utc + "Z");
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const scheduleStatus = (b) => {
  if (!b.start_date && !b.end_date) return null;
  const now = new Date().toISOString().slice(0, 16);
  if (b.start_date && now < b.start_date) return { label: "Gözləyir", cls: "text-blue-400" };
  if (b.end_date && now > b.end_date) return { label: "Bitib", cls: "text-red-400" };
  return { label: "Aktiv dövr", cls: "text-green-400" };
};

export default function BannersPage() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [channelSearch, setChannelSearch] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [aiTextLoading, setAiTextLoading] = useState(false);

  const load = () => adminApi.banners().then(({ data }) => setBanners(data.banners)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Load channel list when 'live' link type is selected
  useEffect(() => {
    if (form.link_type === "live" && channels.length === 0) {
      adminApi.liveChannels().then(({ data }) => setChannels(data.channels)).catch(() => toast.error("Kanal siyahısı yüklənmədi"));
    }
  }, [form.link_type, channels.length]);

  const handleAiImage = async () => {
    if (!aiPrompt.trim()) { toast.error("Əvvəlcə banner təsvirini yazın"); return; }
    setAiImageLoading(true);
    try {
      const { data } = await adminApi.aiImage(aiPrompt.trim());
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Şəkil generasiya olundu");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAiImageLoading(false);
    }
  };

  const handleAiText = async () => {
    if (!aiPrompt.trim()) { toast.error("Əvvəlcə banner təsvirini yazın"); return; }
    setAiTextLoading(true);
    try {
      const { data } = await adminApi.aiText(aiPrompt.trim());
      setForm((f) => ({ ...f, title: data.title || f.title, description: data.description || f.description }));
      toast.success("Mətn generasiya olundu");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAiTextLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await adminApi.uploadImage(file);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Şəkil yükləndi");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.image_url) {
      toast.error("Başlıq və şəkil mütləqdir");
      return;
    }
    setSaving(true);
    try {
      await adminApi.createBanner({ ...form, start_date: toUtc(form.start_date), end_date: toUtc(form.end_date), order: banners.length });
      toast.success("Banner əlavə olundu");
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b) => {
    const { id, ...rest } = b;
    await adminApi.updateBanner(id, { ...rest, active: !b.active });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Banner silinsin?")) return;
    await adminApi.deleteBanner(id);
    toast.success("Silindi");
    load();
  };

  const inputCls = "w-full px-3 py-2 bg-zinc-800/80 border border-zinc-700/40 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/50";

  return (
    <div data-testid="admin-banners-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Hero Bannerlər</h1>
        <button
          data-testid="banner-add-btn"
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg text-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Yeni Banner
        </button>
      </div>

      {showForm && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 mb-6 space-y-3">
          {/* AI generation */}
          <div className="bg-violet-500/5 border border-violet-500/20 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-violet-300">
              <Sparkles className="w-4 h-4" /> Sİ ilə banner hazırla
            </div>
            <textarea
              data-testid="ai-prompt-input"
              placeholder="Banner mövzusunu təsvir edin (məs: Çempionlar Liqası finalı, stadion atmosferi, dramatik işıqlar)"
              rows={2}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className={inputCls}
            />
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="ai-generate-image-btn"
                onClick={handleAiImage}
                disabled={aiImageLoading}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium rounded-lg text-sm transition-colors"
              >
                {aiImageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                {aiImageLoading ? "Şəkil hazırlanır..." : "Şəkli yarat (Sİ)"}
              </button>
              <button
                data-testid="ai-generate-text-btn"
                onClick={handleAiText}
                disabled={aiTextLoading}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600/20 hover:bg-violet-600/30 disabled:opacity-50 text-violet-300 border border-violet-500/30 font-medium rounded-lg text-sm transition-colors"
              >
                {aiTextLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Type className="w-4 h-4" />}
                {aiTextLoading ? "Mətn hazırlanır..." : "Başlıq/təsvir yarat (Sİ)"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="banner-title-input" placeholder="Başlıq *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <input placeholder="Təsvir" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <input data-testid="banner-image-input" placeholder="Şəkil URL *" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} />
            <label className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm cursor-pointer transition-colors flex-shrink-0">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Yüklə
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select data-testid="banner-location-select" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls}>
              <option value="home">Ana səhifə</option>
              <option value="movies">Filmlər</option>
              <option value="series">Seriallar</option>
              <option value="all">Bütün səhifələr</option>
            </select>
            <select value={form.link_type} onChange={(e) => setForm({ ...form, link_type: e.target.value })} className={inputCls}>
              <option value="none">Link yoxdur</option>
              <option value="vod">Film (stream_id)</option>
              <option value="live">Canlı kanal (stream_id)</option>
              <option value="url">Xarici URL</option>
            </select>
            {form.link_type === "live" ? (
              <div className="space-y-1">
                <input
                  data-testid="channel-search-input"
                  placeholder="Kanal axtar..."
                  value={channelSearch}
                  onChange={(e) => setChannelSearch(e.target.value)}
                  className={inputCls}
                />
                <select
                  data-testid="channel-picker-select"
                  value={form.link_value}
                  onChange={(e) => {
                    const ch = channels.find((c) => c.stream_id === e.target.value);
                    setForm((f) => ({ ...f, link_value: e.target.value, title: f.title || (ch ? ch.name : "") }));
                  }}
                  className={inputCls}
                  size={channelSearch ? 5 : 1}
                >
                  <option value="">{channels.length ? `Kanal seçin (${channels.length})` : "Yüklənir..."}</option>
                  {channels
                    .filter((c) => !channelSearch || c.name.toLocaleLowerCase("az").includes(channelSearch.toLocaleLowerCase("az")))
                    .map((c) => (
                      <option key={c.stream_id} value={c.stream_id}>{c.name}</option>
                    ))}
                </select>
              </div>
            ) : form.link_type !== "none" ? (
              <input placeholder={form.link_type === "url" ? "https://..." : "stream_id"} value={form.link_value} onChange={(e) => setForm({ ...form, link_value: e.target.value })} className={inputCls} />
            ) : null}
          </div>
          {form.image_url && <img src={imgSrc(form.image_url)} alt="" className="h-32 rounded-lg object-cover" />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Başlama tarixi (boş = dərhal)</label>
              <input data-testid="banner-start-date" type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Bitmə tarixi (boş = limitsiz)</label>
              <input data-testid="banner-end-date" type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} />
            </div>
          </div>
          <button data-testid="banner-save-btn" onClick={handleSave} disabled={saving} className="px-5 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold rounded-lg text-sm transition-colors">
            {saving ? "Saxlanılır..." : "Yadda saxla"}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {banners.length === 0 && <p className="text-zinc-500 text-sm">Hələ banner yoxdur. Banner əlavə etdikdə ana səhifə hero-su serverdən göstəriləcək.</p>}
        {banners.map((b) => (
          <div key={b.id} data-testid={`banner-row-${b.id}`} className={`flex items-center gap-4 bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 ${!b.active ? "opacity-50" : ""}`}>
            <GripVertical className="w-4 h-4 text-zinc-600 flex-shrink-0" />
            <img src={imgSrc(b.image_url)} alt="" className="w-28 h-16 rounded-lg object-cover flex-shrink-0 bg-zinc-800" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{b.title}</p>
              <p className="text-xs text-zinc-500 truncate">{b.description || "—"}</p>
              <p className="text-[10px] text-zinc-600 mt-0.5">
                <span className="text-amber-500/80">{locationLabels[b.location] || "Ana səhifə"}</span>
                {" · "}{b.link_type !== "none" ? `${b.link_type}: ${b.link_value}` : "Link yoxdur"}
                {(b.start_date || b.end_date) && (() => {
                  const st = scheduleStatus(b);
                  return (
                    <>
                      {" · "}
                      <span className={st?.cls}>{st?.label}</span>
                      {" "}
                      <span className="text-zinc-500">
                        {b.start_date ? toLocal(b.start_date).replace("T", " ") : "∞"} → {b.end_date ? toLocal(b.end_date).replace("T", " ") : "∞"}
                      </span>
                    </>
                  );
                })()}
              </p>
            </div>
            <button onClick={() => toggleActive(b)} className="p-2 text-zinc-400 hover:text-white transition-colors" title={b.active ? "Deaktiv et" : "Aktiv et"}>
              {b.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button data-testid={`banner-delete-${b.id}`} onClick={() => handleDelete(b.id)} className="p-2 text-zinc-400 hover:text-red-400 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
